import { Subscription } from 'rxjs';
import { AfterViewInit, Component, ElementRef, OnDestroy, OnInit, QueryList, ViewChild, ViewChildren } from '@angular/core';
import { skip } from 'rxjs/operators';
import { ActivatedRoute, Router } from '@angular/router';
import { ThemeCardComponent } from '../../../component/card/theme-card/theme-card.component';
import { isPaletteShown, THEME_PALETTES } from '../../../data/palette/palette.data';
import { THEMES } from '../../../data/product/product.data';
import { URLS } from '../../../data/navigation/navigation.data';
import { PaletteColor, PalettePlatform, ThemePalette } from '../../../model/palette/palette.model';
import { Product, isThemeFree } from '../../../model/product/product.model';
import { AnalyticsService } from '../../../service/analytics/analytics.service';
import { RoutingService } from '../../../service/routing/routing.service';
import { UtilService } from '../../../service/util/util.service';

// The /palettes page: the themes-page dashboard, one to one - sticky filter
// sidebar (sort, category, appearance, color, tag, featured, platform,
// pricing), the same poster theme cards with the FLIP animation, and the
// full-width expander row. Only the expander CONTENT is palette-specific:
// the platform downloads (macOS today, Adobe next) and all of its named colors
// as square tiles in a 4-column grid, each linking to its /tools/color page.
//
// The card set is every theme with a palette - all 21. Free themes carry
// their downloadable platforms (macOS color lists); premium themes show the
// palette colors only for now.
@Component( {
  selector: 'app-palettes-page',
  templateUrl: './palettes-page.component.html',
  styleUrls: [ './palettes-page.component.scss' ],
} )
export class PalettesPageComponent implements AfterViewInit, OnInit, OnDestroy {

  URLS: any = URLS;

  allProducts: Product[] = [];
  // Logical filter result (drives the sidebar count).
  filteredProducts: Product[] = [];
  // What the grid actually renders - briefly holds leaving cards too, so
  // they can play their exit animation before being dropped.
  displayedProducts: Product[] = [];
  // Keys of cards currently animating in (kept hidden until the play step).
  enteringKeys = new Set<string>();

  // --- Expander row state (same mechanics as the themes page) ---
  expandedProduct: Product | null = null;
  expanderAfterIndex = -1;
  expanderOpen = false;
  expanderArrowX = 0;
  private expanderCloseTimer: any;

  @ViewChildren( ThemeCardComponent ) themeCards?: QueryList<ThemeCardComponent>;

  // --- FLIP animation state ---
  @ViewChild( 'themesGrid' ) themesGridRef?: ElementRef<HTMLElement>;
  private flipViewReady = false;
  private flipping = false;
  private flipTimers: any[] = [];
  private flipRaf = 0;

  private smallScreenResizeListener?: () => void;

  // Mobile renders the same poster cards, but without the expander row:
  // tapping a card goes straight to its palette page (same as themes).
  isMobileCached = false;

  // theme key -> its palette (colors + downloadable platforms).
  private palettesByKey = new Map<string, ThemePalette>();

  // Filtering
  filterOptions = {
    appearance: [] as string[],
    category: [] as string[],
    featured: [] as string[],
    platform: [] as any[],
    tag: [] as string[],
    color: [] as string[],
    pricing: [] as string[]
  };

  selectedFilters = {
    appearance: [] as string[],
    category: [] as string[],
    featured: [] as string[],
    platform: [] as string[],
    tag: [] as string[],
    color: [] as string[],
    pricing: [] as string[]
  };

  private static readonly APPEARANCE_ORDER = [ 'Light', 'Dark', 'Both' ];

  private static readonly MAIN_COLORS = [
    'Red', 'Orange', 'Yellow', 'Green', 'Teal',
    'Blue', 'Purple', 'Pink', 'Brown', 'Beige',
    'Gray', 'Black', 'Multicolor'
  ];

  private static readonly MAIN_COLOR_SWATCHES: { [ key: string ]: string } = {
    Red: '#E04848',
    Orange: '#F08A3C',
    Yellow: '#F1C232',
    Green: '#54B257',
    Teal: '#1FA28E',
    Blue: '#3D7CC9',
    Purple: '#8A5BC9',
    Pink: '#E371A3',
    Brown: '#8B5A2B',
    Beige: '#E8DBBC',
    Gray: '#9A9A9A',
    Black: '#1A1A1A',
    Multicolor: 'conic-gradient(from 200deg at 50% 50%, #FF3D7F, #FF9F1C, #FFE066, #38B000, #00C2D1, #4361EE, #9D4EDD, #FF3D7F)',
  };

  // Dropdown states
  activeDropdown: string | null = null;

  readonly sortOptions: { value: string, label: string }[] = [
    { value: 'name-asc', label: 'Alphabetical A–Z' },
    { value: 'name-desc', label: 'Alphabetical Z–A' },
    { value: 'newest', label: 'Newest first' },
    { value: 'oldest', label: 'Oldest first' },
  ];
  selectedSort = 'name-asc';
  private sortExplicit = false;

  get selectedSortLabel(): string {
    return this.sortOptions.find( o => o.value === this.selectedSort )?.label || '';
  }

  private static readonly QUERY_PARAM_KEYS: { [ key: string ]: keyof PalettesPageComponent['selectedFilters'] } = {
    appearance: 'appearance',
    category: 'category',
    featured: 'featured',
    platform: 'platform',
    tag: 'tag',
    color: 'color',
    pricing: 'pricing',
  };

  readonly isThemeFree = isThemeFree;

  constructor(
    public utilService: UtilService,
    private route: ActivatedRoute,
    private router: Router,
    private routingService: RoutingService,
    private analyticsService: AnalyticsService,
  ) {
    window.addEventListener( 'click', () => {
      this.activeDropdown = null;
    } );

    // a palette with no app pictures yet is not listed (owner 2026-10-15)
    for ( const palette of THEME_PALETTES.filter( pl => isPaletteShown( pl.key ) ) ) {
      this.palettesByKey.set( palette.key, palette );
    }

    // The card set = the free themes whose palettes ship (palette.data.ts),
    // carrying the full theme product so the cards render one to one with
    // the themes page (covers, screenshots, tags, editorial labels).
    THEMES.forEach( productLine => {
      productLine.products.forEach( product => {
        if ( product.key && this.palettesByKey.has( product.key ) ) {
          product.category = productLine.name;
          this.allProducts.push( product );
        }
      } );
    } );

    this.initializeFilters();
    this.applyFiltersFromQueryParams();
    this.applyFilters();
  }

  /**
   * A link to this page while it is open (the Platform Coverage table's platform headers, owner
   * 2026-10-17) only changes the query string: the filters in it are applied, the grid refreshed and
   * brought into view. The page's own filter clicks write the same filters back, so they change nothing.
   */
  private onQueryParamsChange( params: { [ key: string ]: any } ): void {
    const before = JSON.stringify( this.selectedFilters );
    const sortBefore = this.selectedSort;
    ( Object.keys( this.selectedFilters ) as ( keyof typeof this.selectedFilters )[] )
      .forEach( key => this.selectedFilters[ key ] = [] );
    this.applyFiltersFromQueryParams( params );
    if ( JSON.stringify( this.selectedFilters ) === before && this.selectedSort === sortBefore ) {
      return;
    }
    this.applyFilters();
    this.scrollToTop();
  }

  ngOnInit(): void {
    this.queryParamsSub = this.route.queryParams.pipe( skip( 1 ) ).subscribe( params => this.onQueryParamsChange( params ) );
    this.isMobileCached = this.utilService.isSmallScreen();
    this.smallScreenResizeListener = () => {
      this.isMobileCached = this.utilService.isSmallScreen();
      // The grid reflows on resize: the expander must follow its card to the
      // card's (possibly new) row. On mobile the tiles navigate instead of
      // expanding, so the row just closes.
      if ( this.expandedProduct ) {
        if ( this.isMobileCached ) {
          this.closeExpander( true );
        } else {
          this.reanchorExpander();
        }
      }
    };
    window.addEventListener( 'resize', this.smallScreenResizeListener, { passive: true } );
  }

  ngAfterViewInit(): void {
    // The grid is in the DOM now - filter changes can run the FLIP animation.
    this.flipViewReady = true;

    // Deep links (/palettes#tokyo, e.g. from the home section) open expanded.
    // Mobile has no expander (and must not auto-redirect off the list), so the
    // fragment is ignored there.
    const fragment = this.isMobileCached ? null : this.route.snapshot.fragment;
    if ( fragment ) {
      const index = this.displayedProducts.findIndex( p => p.key === fragment );
      if ( index !== -1 ) {
        setTimeout( () => {
          this.toggleExpander( this.displayedProducts[ index ], index );
        }, 120 );
      }
    }
  }

  paletteOf( product: Product | null ): ThemePalette | null {
    return product?.key ? this.palettesByKey.get( product.key ) ?? null : null;
  }

  /**
   * Seed `selectedFilters` from the URL's query string so deep links like
   * `/palettes?appearance=Dark&category=Cities` open with those chips active.
   */
  private applyFiltersFromQueryParams( params: { [ key: string ]: any } = this.route.snapshot.queryParams ) {
    if ( !params ) {
      return;
    }

    const validOptions: { [ key: string ]: string[] } = {
      appearance: this.filterOptions.appearance,
      category: this.filterOptions.category,
      featured: this.filterOptions.featured,
      platform: this.filterOptions.platform.map( ( p: any ) => p?.name ).filter( ( n: any ) => !!n ),
      tag: this.filterOptions.tag,
      color: this.filterOptions.color,
      pricing: this.filterOptions.pricing,
    };

    Object.keys( PalettesPageComponent.QUERY_PARAM_KEYS ).forEach( paramKey => {
      const filterKey = PalettesPageComponent.QUERY_PARAM_KEYS[ paramKey ];
      const raw = params[ paramKey ];
      if ( raw === undefined || raw === null || raw === '' ) {
        return;
      }

      const incoming: string[] = Array.isArray( raw ) ? raw : String( raw ).split( ',' );
      const allowed = validOptions[ filterKey ] || [];

      const matched = incoming
        .map( v => ( v || '' ).trim() )
        .filter( v => v.length > 0 )
        .map( v => allowed.find( opt => opt.toLowerCase() === v.toLowerCase() ) )
        .filter( ( v ): v is string => !!v );

      if ( matched.length > 0 ) {
        this.selectedFilters[ filterKey ] = [ matched[ 0 ] ];
      }
    } );

    const sortRaw = params[ 'sort' ];
    if ( sortRaw && this.sortOptions.some( o => o.value === sortRaw ) ) {
      this.selectedSort = sortRaw;
      this.sortExplicit = true;
    }
  }

  /** Mirror the active filters back into the URL (replace, not push). */
  private updateQueryParams() {
    const queryParams: { [ key: string ]: string | null } = {};
    Object.keys( PalettesPageComponent.QUERY_PARAM_KEYS ).forEach( paramKey => {
      const filterKey = PalettesPageComponent.QUERY_PARAM_KEYS[ paramKey ];
      const values = this.selectedFilters[ filterKey ];
      queryParams[ paramKey ] = values && values.length > 0 ? values[ 0 ].toLowerCase() : null;
    } );

    queryParams[ 'sort' ] = this.sortExplicit ? this.selectedSort : null;

    this.router.navigate( [], {
      relativeTo: this.route,
      queryParams,
      queryParamsHandling: 'merge',
      replaceUrl: true,
    } );
  }

  /**
   * Card click on the grid: open the full-width palette row under the
   * clicked card's row, or close it when the same card is clicked again.
   */
  toggleExpander( product: Product, index: number ) {
    // Mobile skips the expander row entirely: a tap opens the palette page
    // directly, exactly like the themes grid on small screens.
    if ( this.isMobileCached ) {
      this.analyticsService.logEvent( 'Palette Clicked', {
        palette: product.name,
        key: product.key,
        source: 'palettes-grid',
      } );
      this.goToPalette( product );
      return;
    }

    if ( this.expandedProduct?.key === product.key ) {
      this.closeExpander();
      return;
    }

    this.analyticsService.logEvent( 'Palette Expanded', {
      palette: product.name,
      key: product.key,
    } );

    if ( this.expanderCloseTimer ) {
      clearTimeout( this.expanderCloseTimer );
      this.expanderCloseTimer = undefined;
    }

    const wasOpen = !!this.expandedProduct;
    this.expandedProduct = product;
    this.anchorExpander( index );

    if ( wasOpen ) {
      this.expanderOpen = true;
      this.scrollExpanderIntoView();
      return;
    }

    this.expanderOpen = false;
    let opened = false;
    const open = () => {
      if ( opened ) {
        return;
      }
      opened = true;
      this.expanderOpen = true;
      this.scrollExpanderIntoView();
    };
    requestAnimationFrame( open );
    setTimeout( open, 80 );
  }

  /** Close the detail row - animated by default, instant for layout changes. */
  closeExpander( instant = false ) {
    if ( !this.expandedProduct ) {
      return;
    }
    if ( this.expanderCloseTimer ) {
      clearTimeout( this.expanderCloseTimer );
      this.expanderCloseTimer = undefined;
    }
    this.expanderOpen = false;
    if ( instant || this.prefersReducedMotion() ) {
      this.expandedProduct = null;
      this.expanderAfterIndex = -1;
      return;
    }
    this.expanderCloseTimer = setTimeout( () => {
      this.expandedProduct = null;
      this.expanderAfterIndex = -1;
      this.expanderCloseTimer = undefined;
    }, 600 );
  }

  /** Anchor the detail row after the LAST card of the clicked card's row. */
  private anchorExpander( index: number ) {
    const grid = this.themesGridRef?.nativeElement;
    if ( !grid ) {
      this.expanderAfterIndex = index;
      return;
    }
    const cards = ( Array.from( grid.children ) as HTMLElement[] )
      .filter( el => el.tagName === 'APP-THEME-CARD' );
    const clicked = cards[ index ];
    if ( !clicked ) {
      this.expanderAfterIndex = index;
      return;
    }

    const rowTop = clicked.offsetTop;
    let last = index;
    while ( last + 1 < cards.length && cards[ last + 1 ].offsetTop === rowTop ) {
      last++;
    }
    this.expanderAfterIndex = Math.min( last, this.displayedProducts.length - 1 );
    const trackStart = cards[ 0 ].offsetLeft;
    this.expanderArrowX = clicked.offsetLeft - trackStart + clicked.offsetWidth / 2;
  }

  /** Re-run the row maths for the currently expanded card (e.g. on resize). */
  private reanchorExpander() {
    if ( !this.expandedProduct ) {
      return;
    }
    const index = this.displayedProducts.findIndex( p => p.key === this.expandedProduct!.key );
    if ( index === -1 ) {
      this.closeExpander( true );
      return;
    }
    setTimeout( () => this.anchorExpander( index ), 60 );
  }

  /** Bring the freshly opened row into view once it has its height. */
  private scrollExpanderIntoView() {
    setTimeout( () => {
      const row = this.themesGridRef?.nativeElement.querySelector( '.expander-row' );
      row?.scrollIntoView( { behavior: 'smooth', block: 'nearest' } );
    }, 570 );
  }

  initializeFilters() {
    const categories = new Set<string>();
    const appearances = new Set<string>();
    const tags = new Set<string>();
    const featured = new Set<string>();
    const platforms = new Map<string, PalettePlatform>();

    this.allProducts.forEach( p => {
      if ( p.category ) categories.add( p.category );
      if ( p.appearance ) appearances.add( p.appearance );
      if ( p.tags ) p.tags.forEach( t => tags.add( t ) );
      if ( p.editorialLabel && p.editorialLabel.trim() ) featured.add( p.editorialLabel.trim() );

      // Palette platforms (macOS today, Adobe next) instead of theme
      // marketplaces - a palette page filters by where the palette ships.
      const palette = this.palettesByKey.get( p.key );
      palette?.platforms.forEach( platform => platforms.set( platform.key, platform ) );
    } );

    this.filterOptions.appearance = Array.from( appearances ).sort( ( a, b ) => {
      const ia = PalettesPageComponent.APPEARANCE_ORDER.indexOf( a );
      const ib = PalettesPageComponent.APPEARANCE_ORDER.indexOf( b );
      if ( ia !== -1 && ib !== -1 ) return ia - ib;
      if ( ia !== -1 ) return -1;
      if ( ib !== -1 ) return 1;
      return a.localeCompare( b );
    } );
    this.filterOptions.category = Array.from( categories ).sort();
    this.filterOptions.featured = Array.from( featured ).sort();
    this.filterOptions.color = [ ...PalettesPageComponent.MAIN_COLORS ];
    this.filterOptions.platform = Array.from( platforms.values() )
      .map( platform => ( { name: platform.name, logo: platform.logo } ) );
    this.filterOptions.tag = Array.from( tags ).sort();
    this.filterOptions.pricing = [ 'Free', 'Paid' ];
  }

  /** a palette app's short name in the filter ("Figma", "CorelDRAW", "MacOS"), as the home's palettes box */
  appShort( name: string ): string {
    return name.replace( / Color Palette$| Palette$/, '' );
  }

  toggleFilter( type: string, value: string ) {
    if ( type === 'tag' || type === 'category' || type === 'appearance' || type === 'featured' || type === 'platform' || type === 'color' || type === 'pricing' ) {
      const index = this.selectedFilters[ type ].indexOf( value );
      if ( index >= 0 ) {
        this.selectedFilters[ type ] = [];
      } else {
        this.selectedFilters[ type ] = [ value ];
      }
    }
    this.analyticsService.logEvent( 'Palettes Filtered', {
      type,
      value,
      active: this.selectedFilters[ type ].indexOf( value ) >= 0,
    } );
    this.applyFilters();
    this.updateQueryParams();
    this.scrollToTop();
  }

  toggleDropdown( name: string, event: Event ) {
    event.stopPropagation();
    this.activeDropdown = this.activeDropdown === name ? null : name;
  }

  /** Pick a value from an opened dropdown, then close the dropdown. */
  selectFromDropdown( type: string, value: string ) {
    this.toggleFilter( type, value );
    this.activeDropdown = null;
  }

  /** Look up the platform logo URL by platform name (for selected chips). */
  getPlatformLogo( name: string ): string | null {
    const found = this.filterOptions.platform.find( p => p?.name === name );
    return found?.logo ?? null;
  }

  /** Total number of filter values currently selected across every group. */
  get totalActiveFilters(): number {
    return this.selectedFilters.appearance.length
      + this.selectedFilters.category.length
      + this.selectedFilters.featured.length
      + this.selectedFilters.platform.length
      + this.selectedFilters.tag.length
      + this.selectedFilters.color.length
      + this.selectedFilters.pricing.length;
  }

  /** Reset every filter group and refresh the visible palette list. */
  clearAllFilters() {
    this.selectedFilters = {
      appearance: [],
      category: [],
      featured: [],
      platform: [],
      tag: [],
      color: [],
      pricing: []
    };
    this.analyticsService.logEvent( 'Palettes Filters Cleared', {} );
    this.applyFilters();
    this.updateQueryParams();
    this.scrollToTop();
  }

  /** On a filter change, snap each card's screenshot carousel back to slide 0. */
  private resetCardScreenshots() {
    this.themeCards?.forEach( card => card.resetScreenshot() );
  }

  /** Scroll the page back to the top after a filter change. */
  private scrollToTop() {
    if ( typeof document === 'undefined' ) {
      return;
    }
    const element = document.getElementById( 'head' );
    if ( element ) {
      element.scrollIntoView( { behavior: 'smooth', block: 'start' } );
    } else if ( typeof window !== 'undefined' ) {
      window.scrollTo( { top: 0, behavior: 'smooth' } );
    }
  }

  applyFilters() {
    // The detail row is anchored to a specific grid row; a filter change
    // reshuffles the rows, so close it before measuring the FLIP.
    this.closeExpander( true );
    this.resetCardScreenshots();

    const next = this.computeFilteredProducts();
    this.filteredProducts = next;

    const canAnimate = this.flipViewReady
      && !!this.themesGridRef
      && !this.prefersReducedMotion();

    if ( !canAnimate || this.flipping ) {
      this.clearPendingFlip();
      this.clearFlipStyles();
      this.enteringKeys.clear();
      this.flipping = false;
      this.displayedProducts = next;
      return;
    }

    this.runFlip( next );
  }

  /** Picks the selected sort and re-runs the grid with the FLIP animation. */
  setSort( value: string ) {
    this.activeDropdown = null;
    this.sortExplicit = true;
    if ( this.selectedSort !== value ) {
      this.selectedSort = value;
      this.applyFilters();
    }
    this.analyticsService.logEvent( 'Palettes Sorted', { sort: value } );
    this.updateQueryParams();
  }

  /** Orders a product list by the selected sort option. */
  private sortProducts( list: Product[] ): Product[] {
    const byName = ( a: Product, b: Product ) => ( a.name || '' ).localeCompare( b.name || '' );
    const byDate = ( a: Product, b: Product ) => ( a.releaseDate || '' ).localeCompare( b.releaseDate || '' );
    switch ( this.selectedSort ) {
      case 'name-desc':
        return [ ...list ].sort( ( a, b ) => byName( b, a ) );
      case 'newest':
        return [ ...list ].sort( ( a, b ) => byDate( b, a ) );
      case 'oldest':
        return [ ...list ].sort( ( a, b ) => byDate( a, b ) );
      default:
        return [ ...list ].sort( byName );
    }
  }

  /** Builds the filtered palette list from the currently selected filters. */
  private computeFilteredProducts(): Product[] {
    return this.sortProducts( this.allProducts.filter( p => {
      const matchCategory = this.selectedFilters.category.length === 0 || this.selectedFilters.category.includes( p.category );
      const matchAppearance = this.selectedFilters.appearance.length === 0
        || ( p.appearance !== undefined && this.selectedFilters.appearance.includes( p.appearance ) );
      const matchTag = this.selectedFilters.tag.length === 0 || this.selectedFilters.tag.some( t => p.tags?.includes( t ) );

      const featuredLabel = p.editorialLabel?.trim() || '';
      const matchFeatured = this.selectedFilters.featured.length === 0
        || ( !!featuredLabel && this.selectedFilters.featured.includes( featuredLabel ) );

      const productColors = this.getProductMainColors( p );
      const matchColor = this.selectedFilters.color.length === 0
        || this.selectedFilters.color.some( c => productColors.has( c ) );

      // Platform = where the PALETTE ships (macOS today); pricing = Free.
      const palette = this.palettesByKey.get( p.key );
      const matchPlatform = this.selectedFilters.platform.length === 0
        || !!palette?.platforms.some( platform => this.selectedFilters.platform.includes( platform.name ) );
      // Free = the palette is downloadable today; Paid = a premium theme's
      // palette (colors only for now).
      const matchPricing = this.selectedFilters.pricing.length === 0
        || this.selectedFilters.pricing.includes( isThemeFree( p ) ? 'Free' : 'Paid' );

      return matchCategory && matchAppearance && matchTag && matchFeatured && matchPlatform && matchPricing && matchColor;
    } ) );
  }

  /** Return the main color buckets a theme reads as (mainColors, curated). */
  getProductMainColors( product: Product ): Set<string> {
    return new Set( product.mainColors ?? [] );
  }

  /** Swatch color shown beside a main-color label in the dropdown / chip. */
  getMainColorSwatch( name: string ): string {
    return PalettesPageComponent.MAIN_COLOR_SWATCHES[ name ] || '#888';
  }

  /**
   * FLIP transition: cards that still match glide from their old grid slot to
   * the new one, filtered-out cards fade/shrink away, new cards fade in.
   */
  private runFlip( next: Product[] ) {
    const grid = this.themesGridRef!.nativeElement;
    this.flipping = true;

    const newKeys = new Set( next.map( p => p.key ) );
    const oldKeys = new Set( this.displayedProducts.map( p => p.key ) );

    const oldRects = new Map<string, DOMRect>();
    const cards = Array.from( grid.children ) as HTMLElement[];
    for ( const el of cards ) {
      const k = el.getAttribute( 'data-key' );
      if ( k ) {
        oldRects.set( k, el.getBoundingClientRect() );
      }
    }
    const gridRect = grid.getBoundingClientRect();

    const leaving = this.displayedProducts.filter( p => !newKeys.has( p.key ) );
    const leavingKeys = new Set( leaving.map( p => p.key ) );
    this.enteringKeys = new Set( next.filter( p => !oldKeys.has( p.key ) ).map( p => p.key ) );

    for ( const el of cards ) {
      const k = el.getAttribute( 'data-key' );
      if ( k && leavingKeys.has( k ) ) {
        const r = oldRects.get( k )!;
        el.classList.add( 'flip-leaving' );
        const s = el.style;
        s.position = 'absolute';
        s.left = ( r.left - gridRect.left ) + 'px';
        s.top = ( r.top - gridRect.top ) + 'px';
        s.width = r.width + 'px';
        s.height = r.height + 'px';
        s.margin = '0';
        s.zIndex = '1';
        s.pointerEvents = 'none';
      }
    }

    this.displayedProducts = [ ...next, ...leaving ];

    this.flipRaf = requestAnimationFrame(
      () => this.playFlip( grid, oldRects, leavingKeys, next )
    );
  }

  private playFlip( grid: HTMLElement, oldRects: Map<string, DOMRect>,
                    leavingKeys: Set<string>, next: Product[] ) {
    const cards = Array.from( grid.children ) as HTMLElement[];
    const movers: HTMLElement[] = [];
    const entering: HTMLElement[] = [];

    for ( const el of cards ) {
      const k = el.getAttribute( 'data-key' );
      if ( !k ) {
        continue;
      }

      if ( leavingKeys.has( k ) ) {
        el.style.transition = 'opacity 220ms ease, transform 220ms ease';
        el.style.opacity = '0';
        el.style.transform = 'scale(0.92)';
        continue;
      }

      if ( this.enteringKeys.has( k ) ) {
        el.style.transition = 'opacity 380ms ease 140ms';
        entering.push( el );
        continue;
      }

      const oldR = oldRects.get( k );
      if ( !oldR ) {
        continue;
      }
      const newR = el.getBoundingClientRect();
      const dx = oldR.left - newR.left;
      const dy = oldR.top - newR.top;
      if ( Math.abs( dx ) < 0.5 && Math.abs( dy ) < 0.5 ) {
        continue;
      }
      el.style.transition = 'none';
      el.style.transform = `translate(${ dx }px, ${ dy }px)`;
      movers.push( el );
    }

    this.flipRaf = requestAnimationFrame( () => {
      for ( const el of movers ) {
        el.style.transition = 'transform 440ms cubic-bezier(0.22, 1, 0.36, 1)';
        el.style.transform = 'translate(0, 0)';
      }
      for ( const el of entering ) {
        el.style.opacity = '1';
      }
    } );

    this.flipTimers.push( window.setTimeout( () => this.endFlip( next ), 620 ) );
  }

  private endFlip( next: Product[] ) {
    this.clearPendingFlip();
    this.displayedProducts = next;
    this.enteringKeys.clear();
    this.clearFlipStyles();
    this.flipping = false;
  }

  private clearPendingFlip() {
    this.flipTimers.forEach( t => clearTimeout( t ) );
    this.flipTimers = [];
    if ( this.flipRaf ) {
      cancelAnimationFrame( this.flipRaf );
      this.flipRaf = 0;
    }
  }

  private clearFlipStyles() {
    const grid = this.themesGridRef?.nativeElement;
    if ( !grid ) {
      return;
    }
    for ( const el of Array.from( grid.children ) as HTMLElement[] ) {
      el.classList.remove( 'flip-leaving' );
      [ 'transition', 'transform', 'opacity', 'position', 'left', 'top',
        'width', 'height', 'margin', 'z-index', 'pointer-events' ]
        .forEach( prop => el.style.removeProperty( prop ) );
    }
  }

  private prefersReducedMotion(): boolean {
    return typeof window !== 'undefined'
      && typeof window.matchMedia === 'function'
      && window.matchMedia( '(prefers-reduced-motion: reduce)' ).matches;
  }

  /** Stable identity for *ngFor so the FLIP animation targets the right cards. */
  trackByKey( _index: number, product: Product ): string {
    return product.key;
  }

  trackByHex( _index: number, color: PaletteColor ): string {
    return color.hex;
  }

  goToPalette( product: Product ): void {
    this.routingService.redirect( URLS.colorPalette, {
      product: product.key,
      category: ( product.category || '' ).toLowerCase(),
    } );
  }

  private queryParamsSub?: Subscription;

  ngOnDestroy(): void {
    this.queryParamsSub?.unsubscribe();
    if ( this.smallScreenResizeListener ) {
      window.removeEventListener( 'resize', this.smallScreenResizeListener );
    }
    if ( this.expanderCloseTimer ) {
      clearTimeout( this.expanderCloseTimer );
    }
    this.clearPendingFlip();
  }
}
